import { IChartContainer } from "../models/base/chart-container.interface";
import { LimitAlertModel } from "../models/limit-alert/limit-alert.model";
import { IComponent } from "./component.interface";

export class LimitAlertPlotLineComponent implements IComponent {
    public id = '';

    public moveMode = false;
    public updatedPrice: number | null = null;
    public selected = false;

    private base: IChartContainer;
    private model: LimitAlertModel;
    private plotLine: any = null;
    private plotLineClone: any = null;
    private cloneId = '';

    constructor(base: IChartContainer, limitAlert: LimitAlertModel) {
        this.base = base;
        this.model = limitAlert;
        this.id = 'limit-alert-' + this.model.id;
        this.cloneId = this.id + '-clone';

        this.base.tiChartSeries?.subscribeExtremeChange(() => {
            this.setIconPosition();
        });
        this.base.tiChartSeries?.subscribeRedrawChange(() => {
            this.setIconPosition();
        });
        this.createSytle();
        this.init();
        this.onEvents();
        this.onDeletePress();
        this.onBodyClick();
    }

    public remove() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        yAxis.removePlotLine(this.id);
        yAxis.removePlotLine(this.cloneId);
        this.removeStyle();
    }

    private removeStyle() {
        const highChartStyle = this.base.tiChartSeries?.element?.findFirst('.style-' + this.id);
        if (highChartStyle) {
            highChartStyle.remove();
        }
    }

    private createSytle() {
        this.removeStyle();
        const className = this.id;
        let styleTag = `<style>`;
        styleTag += `.highcharts-plot-line.${className} {stroke-width: 10; stroke: transparent; cursor: move;}`;
        styleTag += `.highcharts-plot-line.${className}-clone {stroke: ${this.model.color.toRGB()};}`;
        styleTag += `</style>`;

        const chartStyleContainer = `<div class="style-${className}">${styleTag}</div>`;
        this.base.tiChartSeries?.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    private init() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (yAxis) {
            yAxis.addPlotLine({
                value: this.model.price,
                className: this.id,
                width: 1,
                id: this.id,
                zIndex: 13,
                label: {
                    formatter: () => {
                        return ' ';
                    },
                    className: 'label-' + this.id,
                    useHTML: true
                },
                events: {
                    mouseover: () => {
                        this.showTooltip();
                    },
                    mousedown: () => {
                        this.moveMode = true;
                    }
                }
            });

            yAxis.addPlotLine({
                value: this.model.price,
                className: this.cloneId,
                id: this.cloneId,
                zIndex: 12
            });

            this.bindEvents();
            this.setIconPosition();
        }
    }

    private onEvents() {
        this.base.domEvents.onMouseMove((event: any) => {
            if (!this.moveMode || !this.base.tiChartSeries) {
                return;
            }

            const yAxis: any = this.base.tiChartSeries.highStockChart.yAxis[0];
            if (!yAxis) {
                return;
            }

            const pointer = this.base.tiChartSeries.highStockChart.pointer.normalize(event);
            
            let yValue = 0;

            if (this.base.pnpMode) {
                yValue = yAxis.toValue(pointer.y - this.base.tiChartSeries.element.offsetTop);
            } else {
                yValue = yAxis.toValue(pointer.y);
            }

            this.model.price = yValue;
            this.updatedPrice = yValue;
            yAxis.removePlotLine(this.id);
            yAxis.addPlotLine({
                value: yValue,
                className: this.id,
                width: 1,
                id: this.id,
                zIndex: 13,
                label: {
                    formatter: () => {
                        return '&nbsp;';
                    },
                    className: 'label-' + this.id,
                    useHTML: true,
                },
                events: {
                    mouseover: () => {
                        this.showTooltip();
                    },
                    mousedown: () => {
                        this.moveMode = true;
                    }
                }
            });

            yAxis.removePlotLine(this.cloneId);
            yAxis.addPlotLine({
                value: this.model.price,
                className: this.cloneId,
                id: this.cloneId,
                zIndex: 12
            });

            this.bindEvents();
            this.setIconPosition();
        });

        this.base.tiChartSeries.element.addEventListener('mouseup', (e: any) => {
            if (!this.moveMode) {
                return;
            }

            this.moveMode = false;
            this.unselectPlotLine();
            this.base.chartService.requestUpdateLimitAlert(this.model.id, this.model.price);
        });
    }

    private onDeletePress() {
        this.base.domEvents.onDeletePress(() => {
            console.log('requestRemoveLimitAlert ', this.selected, this.model)
            if (this.selected) {
                this.base.chartService.requestRemoveLimitAlert(this.model.id);
                this.unselectPlotLine();
            }
        });
    }

    private onBodyClick() {
        this.base.domEvents.onScreenClick((event: any) => {
            if ((!event.target || !event.target.classList.contains(this.id))
                && !(this.selected && event.target && event.target.classList.contains('highcharts-annotation-remove-button'))) {
                this.unselectPlotLine();
            }
        });
    }

    private selectPlotLine() {
        this.selected = true;
        this.plotLine && this.plotLine.classList.add('selected');
        this.plotLineClone && this.plotLineClone.classList.add('selected');
    }

    private unselectPlotLine() {
        this.selected = false;
        this.plotLine && this.plotLine.classList.remove('selected');
        this.plotLineClone && this.plotLineClone.classList.remove('selected');
    }

    private bindEvents() {
        this.plotLine = this.base.tiChartSeries.element.findFirst('.' + this.id);
        this.plotLineClone = this.base.tiChartSeries.element.findFirst('.' + this.id + '-clone');
        if (this.plotLine) {
            this.plotLine.addEventListener('click', () => {
                if (this.plotLine.classList.contains('selected')) {
                    this.unselectPlotLine();
                } else {
                    this.selectPlotLine();
                }
            });
        }

        if (this.selected) {
            this.selectPlotLine();
        }
    }

    private setIconPosition() {
        const chartStyle = this.base.tiChartSeries?.chartStyleSettings;
        this.base.tiChartSeries.element.findFirst(`.highcharts-plot-line-label.label-${this.id}`)?.ifExistThen((el: HTMLElement) => {
            el.querySelectorAll('img').forEach((i: HTMLImageElement) => {
                i.remove();
            });
            
            let left = 0;
            if (this.base.pnpMode) {
                left =  this.base.tiChartSeries.element.clientWidth - 77;

                if (!this.base.marketingWebChartMode) {
                    left =  this.base.tiChartSeries.element.clientWidth - 78;
                }
            } else {
                left = this.base.tiChartSeries.element.clientWidth - (chartStyle?.showSmartRiskLevel ? 112 : 82);
            }
            
            const style = `position: absolute;left: ${left}px; top: 12px;`;
            el.innerHTML = `<img src="${this.model.getImage(this.base.basePath)}" style="${style}" title="${this.model.tooltip}" />`;
        });
    }

    private showTooltip() {
        if (this.plotLine) {
            const titleElement = document.createElementNS('http://www.w3.org/2000/svg', 'title');
            titleElement.textContent = this.model.tooltip;

            const plotLineElement = this.plotLine as SVGPathElement;
            const groupElement = plotLineElement.parentNode;
            if (groupElement) {
                groupElement.querySelectorAll('title').forEach((t: Element) => {
                    t.remove();
                });
                groupElement?.append(titleElement);
            }
        }
    }
}